import {
  Button,
  ButtonGroup,
  cn,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Tooltip,
} from '@heroui/react'
import { produce } from 'immer'
import { Plus, Save, Trash, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useToggle } from 'usehooks-ts'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import EditRowButton from './EditRowButton'
import { usePatchSchema, useSchema } from './utils'
import VariableTable from './VariableTable'

const operators = [{ label: 'is', value: '===' }, { label: 'is not', value: '!==' }]

function ConditionForm({ condition = null, darkMode = false, inline = false }) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')
  const { data: selectedSchema } = useSchema(id)

  const allVars = useMemo(() => selectedSchema?.schema?.variables ?? [], [selectedSchema])

  return (
    <>
      <Select
        size="sm"
        variant="underlined"
        radius="sm"
        name="variable"
        placeholder="Select a variable"
        items={allVars}
        className={cn(inline ? 'w-full' : 'w-48')}        
        description={inline ? 'The existing variable to evaluate in the condition.' : undefined}
        defaultSelectedKeys={[condition?.variable]}
        classNames={{
          value: cn(darkMode && '!text-white', !inline && 'font-mono'),
        }}
      >
        {variable => <SelectItem key={variable.name}>{variable.name}</SelectItem>}
      </Select>
      <Select
        size="sm"
        variant="underlined"
        radius="sm"
        name="operator"
        className={cn(inline ? 'w-full' : 'w-24')}        
        description={inline ? 'The operator to use for the evaluation.' : undefined}
        placeholder="Operator"
        items={operators}
        defaultSelectedKeys={[condition?.operator]}
        classNames={{
          value: cn(darkMode && '!text-white', !inline && 'font-mono'),
        }}
      >
        {operator => <SelectItem key={operator.value}>{operator.label}</SelectItem>}
      </Select>
      <Input
        size="sm"
        className={cn(inline ? 'w-full' : 'w-48')}
        name="value"
        radius="sm"        
        description={inline ? 'The value to compare the evaluated variable to.' : undefined}
        variant="underlined"
        placeholder="Value"
        defaultValue={condition?.value}
        classNames={{
          input: cn(darkMode && '!text-white', !inline && 'font-mono'),
        }}
      />
    </>
  )
}

function ConditionItem({ condition, index }) {
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { variable, operator, value } = condition?.condition || {}
  const { data: selectedSchema } = useSchema(id)
  const { mutate: patchSchema } = usePatchSchema()

  async function handleSaveCondition(e) {
    e.preventDefault()
    const updatedIf = Object.fromEntries(new FormData(e.currentTarget))

    const updatedSchema = produce(selectedSchema, (draft) => {
      draft.schema.conditions[index] = {
        ...draft.schema.conditions[index],
        condition: updatedIf,
      }
    })

    await patchSchema(updatedSchema)
    if (patchSchema.error) {
      toast.error('Failed to update condition')
    }
    else {
      toast.success('Condition updated successfully')
    }
    setIsEditing(false)
  }

  async function handleDeleteCondition() {
    const updatedSchema = produce(selectedSchema, (draft) => {
      draft.schema.conditions.splice(index, 1)
    })

    await patchSchema(updatedSchema)

    if (patchSchema.error) {
      toast.error('Failed to delete condition')
    }
    else {
      toast.success('Condition deleted successfully')
    }
  }

  return (
    <div className="flex flex-col w-full">
      <span className="flex flex-row gap-2 bg-black text-white w-fit p-2 items-center">
        {isEditing
          ? (
              <Form onSubmit={handleSaveCondition} className="contents">
                <ConditionForm condition={condition?.condition} darkMode />
                <ButtonGroup>
                  <Tooltip content="Save">
                    <Button type="submit" size="sm" radius="sm" variant="light" isIconOnly color="default">
                      <Save size={16} className="text-white" />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Cancel">
                    <Button onPress={() => setIsEditing(false)} size="sm" radius="sm" variant="light" isIconOnly color="default">
                      <X size={16} className="text-gray-500" />
                    </Button>
                  </Tooltip>
                  <ConfirmDeleteModal
                    isOpen={isDeleting}
                    onOpenChange={setIsDeleting}
                    onConfirm={handleDeleteCondition}
                    title="Delete Condition"
                    message="Are you sure you want to delete this condition?"
                  >
                    <Tooltip content="Delete">
                      <Button onPress={() => setIsDeleting(true)} size="sm" radius="sm" variant="light" isIconOnly color="warning">
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </Tooltip>
                  </ConfirmDeleteModal>
                </ButtonGroup>
              </Form>
            )
          : (
              <>
                <p className="text-sm font-medium font-mono">{variable}</p>
                <p className="text-sm">{operator === '===' ? 'is' : 'is not'}</p>
                <p className="text-sm font-medium">
                  "
                  {value}
                  "
                </p>
                <EditRowButton className="h-full text-gray-500 py-2" radius="none" variant="light" color="default" onPress={() => setIsEditing(!isEditing)} />
              </>
            )}

      </span>
      <VariableTable conditionIndex={index} variables={condition.variables} />
    </div>
  )
}

function CreateConditionModal({ isOpen, onOpenChange }) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')
  const { mutate: patchSchema } = usePatchSchema()

  const { data: selectedSchema } = useSchema(id)

  const onSubmit = async (e) => {
    e.preventDefault()

    const newIf = Object.fromEntries(new FormData(e.currentTarget))

    const newCondition = {
      condition: newIf,
      variables: [],
    }

    const updatedSchema = produce(selectedSchema, (draft) => {
      draft.schema.conditions = [...(draft.schema.conditions ?? []), newCondition]
    })

    await patchSchema(updatedSchema)
    if (patchSchema.error) {
      toast.error('Failed to create condition')
    }
    else {
      toast.success('Condition created successfully')
    }

    onOpenChange()
  }

  return (
    <Modal
      radius="sm"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      classNames={{
        footer: 'ml-auto',
        body: 'flex flex-col gap-5 w-full pt-4',
        header: 'border-b border-gray-200 bg-[#18181b] w-full',
      }}
    >
      <ModalContent>
        {onClose => (
          <Form onSubmit={onSubmit}>
            <ModalHeader className="flex flex-col gap-1">
              <h2 className="text-2xl font-taurus text-white font-semibold flex items-center gap-3">
                Create new condition
              </h2>
              <p className="text-xs font-taurus font-light tracking-wider text-foreground-400">
                Specify logic for extracting citations conditionally.
              </p>
            </ModalHeader>
            <ModalBody>
              <ConditionForm inline />
            </ModalBody>
            <ModalFooter>
              <Button color="danger" radius="sm" variant="light" onPress={onClose}>
                Close
              </Button>
              <Button color="primary" radius="sm" type="submit">
                Create
              </Button>
            </ModalFooter>
          </Form>
        )}
      </ModalContent>
    </Modal>
  )
}

export default function Conditions() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { data: selectedSchema } = useSchema(id)
  const [isCreating, toggleCreating] = useToggle(false)

  const conditions = useMemo(() => selectedSchema?.schema?.conditions ?? [], [selectedSchema])

  return (
    <div className="flex flex-col gap-14 w-full items-start">
      {!conditions?.length
        ? (
            <div className="w-full flex flex-col items-center mt-24">
              <h1 className="text-3xl tracking-tighter font-semibold mb-6">
                No conditions yet.
              </h1>
            </div>
          )
        : (conditions.map((condition, index) => (
            <ConditionItem
              key={index}
              condition={condition}
              index={index}
            />
          )))}
      <Button
        disableRipple
        onPress={toggleCreating}
        variant="bordered"
        size="md"
        radius="sm"
        fullWidth
        className="mt-10"
      >
        <Plus size={16} />
        Add condition
      </Button>
      <CreateConditionModal isOpen={isCreating} onOpenChange={toggleCreating} />
    </div>
  )
}
